import { Box, HStack, VStack } from "@chakra-ui/react";
import {
  AddCircle as AddCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { FieldArray, useField } from "formik";
import * as React from "react";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { SymbolOutlineButton } from "~/src/components/common/Button";
import { FormControl } from "~/src/components/common/forms/FormControl";
import {
  FormikSelectField,
  type FormikSelectFieldProps,
} from "~/src/components/common/forms/FormikSelectField";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";
import { IconButton } from "~/src/components/common/IconButton";
import { Switch } from "~/src/components/common/Switch";
import { useOpenState } from "~/src/lib/hooks";

type TargetContentSelectProps = Omit<
  FormikSelectFieldProps,
  "name" | "options"
>;

type Props = {
  name: string;
  label: string;
  selectProps?: TargetContentSelectProps;
};

export const TargetContentListField: React.VFC<Props> = ({
  name,
  label,
  selectProps,
}) => {
  const [contents, , { setValue }] = useField<string[]>(name);

  const { isOpen, onOpen, onClose } = useOpenState(
    contents.value ? contents.value.length > 0 : false
  );

  const handleChange = React.useCallback(() => {
    if (isOpen) {
      onClose();
      setValue([]);
    } else {
      onOpen();
    }
  }, [isOpen, onClose, onOpen, setValue]);

  const hideLabel = label === "";

  return (
    <FieldArray
      name={name}
      // eslint-disable-next-line react/jsx-no-bind
      render={(arrayHelpers) => (
        <FormControl label={label} hideLabel={hideLabel}>
          <Box width="100%" display="block">
            <Switch isChecked={isOpen} onChange={handleChange} />
          </Box>
          <VStack width="100%">
            {isOpen && (
              <>
                {contents.value &&
                  contents.value.length > 0 &&
                  contents.value.map((id, index) => (
                    <HStack key={index} width="100%">
                      <Box flex={1}>
                        <FormikSelectField
                          name={`${name}.${index}.targetKind`}
                          options={
                            GraphQLEnums.PopupGroupDisplayTargetContentKind
                          }
                          {...selectProps}
                        />
                      </Box>

                      <Box flex={2}>
                        <FormikTextField
                          type="text"
                          mode="fill"
                          name={`${name}.${index}.targetValue`}
                          placeholder=""
                        />
                      </Box>
                      <Box>
                        <IconButton
                          size="24px"
                          icon={<DeleteOutlineIcon />}
                          // eslint-disable-next-line react/jsx-no-bind
                          onClick={() => arrayHelpers.remove(index)}
                        />
                      </Box>
                    </HStack>
                  ))}
                <Box width="100%">
                  <SymbolOutlineButton
                    icon={<AddCircleIcon />}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => arrayHelpers.push({})}
                  >
                    条件を追加
                  </SymbolOutlineButton>
                </Box>
              </>
            )}
          </VStack>
        </FormControl>
      )}
    />
  );
};
